import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import styles from './Text.scss';

/* eslint-disable sort-keys */
const variantMap = {
    'display-xxl': 'h1',
    'display-xl': 'h2',
    'display-l': 'h3',
    'display-m': 'h4',
    'display-s': 'h5',
    'display-xs': 'h6',
    'text-xl': 'p',
    'text-l': 'p',
    'text-m': 'p',
    'text-s': 'p',
    'text-xs': 'p',
};
/* eslint-enable sort-keys */

function Text(props) {
    const {
        align = 'inherit',
        bold = false,
        className,
        color = 'initial',
        component: ComponentProp,
        noWrap = false,
        variant = 'text-m',
        ...otherProps
    } = props;

    const Component = ComponentProp || variantMap[variant];

    return (
        <Component
            className={classNames(
                styles.root,
                {
                    [styles[`color-${color}`]]: color !== 'initial',
                    [styles[`align-${align}`]]: align !== 'inherit',
                    [styles[variant]]: variant !== 'inherit',
                    [styles['no-wrap']]: noWrap,
                    [styles.bold]: bold,
                },
                className,
            )}
            {...otherProps}
        />
    );
}

Text.propTypes = {
    /**
     * Set the text-align on the component.
     */
    align: PropTypes.oneOf([
        'inherit',
        'left',
        'center',
        'right',
        'justify',
    ]),
    /**
     * Set the font-weight.
     */
    bold: PropTypes.bool,
    /**
     * The content of the component.
     */
    children: PropTypes.node.isRequired,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * The color of the component. It supports those theme colors that make sense for this component.
     */
    color: PropTypes.oneOf([
        'initial',
        'inherit',
        'primary',
        'secondary',
        'link',
        'success',
        'error',
        'info',
        'warning',
    ]),
    /**
     * The component used for the root node.
     * Either a string to use a HTML element or a component.
     */
    component: PropTypes.elementType,
    /**
     * If `true`, the text will not wrap, but instead will truncate with a text overflow ellipsis.
     */
    noWrap: PropTypes.bool,
    /**
     * Used to control the text styling.
     */
    variant: PropTypes.oneOf([
        'display-xxl',
        'display-xl',
        'display-l',
        'display-m',
        'display-s',
        'display-xs',
        'text-xl',
        'text-l',
        'text-m',
        'text-s',
        'text-xs',
        'inherit',
    ]),
};

export default Text;
